import { apiFetch, getApiUrl } from "../auth/apiClient";

// Client for the maintenance-ticket workflow. Backs the web Tickets tab;
// the backend (app/api/tickets.py) scopes the list per role and enforces
// every transition, so the UI only has to surface the right buttons.
export const ticketsApi = {
  listCategories: () =>
    apiFetch("/api/tickets/categories", { authRequired: true }),
  createCategory: (body) =>
    apiFetch("/api/tickets/categories", { method: "POST", authRequired: true, body }),
  updateCategory: (id, body) =>
    apiFetch(`/api/tickets/categories/${id}`, { method: "PATCH", authRequired: true, body }),
  deleteCategory: (id) =>
    apiFetch(`/api/tickets/categories/${id}`, { method: "DELETE", authRequired: true }),

  list: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    if (params.category_id) qs.set("category_id", params.category_id);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch(`/api/tickets${suffix}`, { authRequired: true });
  },

  get: (id) => apiFetch(`/api/tickets/${id}`, { authRequired: true }),

  create: (body) =>
    apiFetch("/api/tickets", { method: "POST", authRequired: true, body }),

  // Edit a ticket's details (title, category, priority, description,
  // availability). Raiser or FM only; closed/cancelled are immutable.
  update: (id, body) =>
    apiFetch(`/api/tickets/${id}`, { method: "PATCH", authRequired: true, body }),

  // Add a comment to a ticket's thread, with any number of images and/or
  // videos. ``files`` is an array of File objects; when present the call goes
  // out as multipart (repeated ``media`` fields), else as a JSON text body.
  reply: (id, { body, files } = {}) => {
    if (files && files.length) {
      const fd = new FormData();
      if (body) fd.append("body", body);
      files.forEach((f) => fd.append("media", f));
      return apiFetch(`/api/tickets/${id}/replies`, {
        method: "POST",
        authRequired: true,
        formData: fd,
      });
    }
    return apiFetch(`/api/tickets/${id}/replies`, {
      method: "POST",
      authRequired: true,
      body: { body },
    });
  },

  // Resolve an attachment's signed (relative) URL to a fetchable absolute one.
  mediaSrc: (relativeUrl) => `${getApiUrl()}${relativeUrl}`,

  // ---- FM ----
  // body: { due_at } (ISO date) or { hours }
  setSla: (id, body) =>
    apiFetch(`/api/tickets/${id}/sla`, {
      method: "POST",
      authRequired: true,
      body,
    }),
  assignableStaff: (id, override = false) =>
    apiFetch(`/api/tickets/${id}/assignable-staff?override=${override}`, {
      authRequired: true,
    }),
  assign: (id, staffId) =>
    apiFetch(`/api/tickets/${id}/assign`, {
      method: "POST",
      authRequired: true,
      body: { staff_id: staffId },
    }),
  closeOnBehalf: (id, approvalNote) =>
    apiFetch(`/api/tickets/${id}/close-on-behalf`, {
      method: "POST",
      authRequired: true,
      body: { approval_note: approvalNote },
    }),
  // FM override: force-close / force-cancel from any non-terminal phase
  // (note required, audited).
  forceClose: (id, note) =>
    apiFetch(`/api/tickets/${id}/force-close`, {
      method: "POST",
      authRequired: true,
      body: { note },
    }),
  forceCancel: (id, note) =>
    apiFetch(`/api/tickets/${id}/force-cancel`, {
      method: "POST",
      authRequired: true,
      body: { note },
    }),

  // ---- Worker ----
  start: (id) =>
    apiFetch(`/api/tickets/${id}/start`, { method: "POST", authRequired: true }),
  done: (id) =>
    apiFetch(`/api/tickets/${id}/done`, { method: "POST", authRequired: true }),

  // ---- Resident (raiser) ----
  confirm: (id, body) =>
    apiFetch(`/api/tickets/${id}/confirm`, {
      method: "POST",
      authRequired: true,
      body: body || {},
    }),
  notFixed: (id, comment) =>
    apiFetch(`/api/tickets/${id}/not-fixed`, {
      method: "POST",
      authRequired: true,
      body: { comment },
    }),
  cancel: (id) =>
    apiFetch(`/api/tickets/${id}/cancel`, {
      method: "POST",
      authRequired: true,
    }),

  // ---- Either ----
  reopen: (id) =>
    apiFetch(`/api/tickets/${id}/reopen`, {
      method: "POST",
      authRequired: true,
    }),
};

// Phase → label + tone for the pills.
export const PHASE_META = {
  triage: { label: "Not assigned", cls: "tk-ph-triage" },
  todo: { label: "To do", cls: "tk-ph-todo" },
  in_progress: { label: "In progress", cls: "tk-ph-progress" },
  owner_review: { label: "Awaiting your OK", cls: "tk-ph-review" },
  closed: { label: "Closed", cls: "tk-ph-closed" },
  cancelled: { label: "Cancelled", cls: "tk-ph-cancelled" },
};

export const PRIORITY_META = {
  P0: { label: "Urgent", cls: "tk-pri-p0" },
  P1: { label: "High", cls: "tk-pri-p1" },
  P2: { label: "Normal", cls: "tk-pri-p2" },
};
